import React, { useEffect, useState } from 'react'
import { InputNumber, Slider } from 'antd'
import { PublicReportService } from 'api/http'
import { useLanguage } from 'context/LanguageContext'
import { PLATFORM_SHARE } from './calc'
import { fill, useFactsFormat } from './factsFormat'
import rccTranslations from './rccTranslations'
import s from './Facts.module.scss'

/** Цена покупки по умолчанию, пока курс не загрузился. */
const FALLBACK_BUY_PRICE = 3.5
const GROWTH_ROWS = [1, 2, 5, 10]

// Курс один на всю страницу — запрашиваем один раз.
let pricePromise: Promise<number | null> | null = null

const useRccPrice = () => {
    const [price, setPrice] = useState<number | null>(null)
    useEffect(() => {
        if (!pricePromise) {
            pricePromise = PublicReportService.getRccPrice()
                .then(r => r.price)
                .catch(() => {
                    pricePromise = null
                    return null
                })
        }
        let alive = true
        pricePromise.then(p => alive && setPrice(p))
        return () => {
            alive = false
        }
    }, [])
    return price
}

interface Props {
    /** Прибыль робота в день (до деления 50/50). */
    robotDay: number
    currency: string
    idPrefix: string
}

/**
 * Куда идёт прибыль: вся — на счёт биржи, комиссия 50% — токенами RCC
 * по текущему курсу (как в роботе: прибыль × 50% / курс RCC).  Раскрывается
 * пример: как рост курса RCC снижает комиссию для купленных раньше токенов.
 */
export const RccCommission: React.FC<Props> = ({
    robotDay,
    currency,
    idPrefix,
}) => {
    const { language } = useLanguage()
    const t = rccTranslations[language] ?? rccTranslations.en
    const { fmt, money } = useFactsFormat(currency)
    const price = useRccPrice()
    const [open, setOpen] = useState(false)
    const [buy, setBuy] = useState<number | null>(null)
    const [growth, setGrowth] = useState(1)

    if (robotDay <= 0) return null
    const fee = robotDay * PLATFORM_SHARE
    const usd = (v: number) => `$${fmt(v, v < 10 ? 2 : 0)}`
    const buyPrice = buy ?? price ?? FALLBACK_BUY_PRICE
    const at = (g: number) => ({
        rate: buyPrice * g,
        tokens: fee / (buyPrice * g),
        pct: (PLATFORM_SHARE * 100) / g,
    })
    const now = at(growth)
    const tokens = (v: number) => fmt(v, v < 100 ? 1 : 0)

    return (
        <div className={s.rcc}>
            <dl className={s.rccSplit}>
                <div>
                    <dt>{t.toExchange}</dt>
                    <dd>{money(robotDay)}</dd>
                    <span>{t.toExchangeHint}</span>
                </div>
                <div>
                    <dt>{t.fee}</dt>
                    <dd className={s.rccFee}>
                        {price
                            ? `≈ ${tokens(fee / price)} RCC`
                            : money(fee)}
                    </dd>
                    {price && (
                        <span>{fill(t.feeHint, { price: usd(price) })}</span>
                    )}
                </div>
            </dl>
            <p className={s.rccStays}>{t.stays}</p>

            <button
                type="button"
                className={s.rccToggle}
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                {t.growTitle} {open ? '▴' : '▾'}
            </button>

            {open && (
                <div className={s.rccMore}>
                    <p className={s.muted}>{t.growText}</p>

                    <div className={s.knob}>
                        <div className={s.knobHead}>
                            <label htmlFor={`${idPrefix}-rcc-buy`}>
                                {t.buyPrice}
                            </label>
                            <InputNumber
                                id={`${idPrefix}-rcc-buy`}
                                className={s.depositInput}
                                size="small"
                                value={buyPrice}
                                min={0.01}
                                step={0.1}
                                precision={2}
                                prefix="$"
                                onChange={v => v && setBuy(Number(v))}
                            />
                        </div>
                    </div>

                    <div className={s.knob}>
                        <div className={s.knobHead}>
                            <label htmlFor={`${idPrefix}-rcc-growth`}>
                                {t.nowPrice}
                            </label>
                            <b>
                                {usd(now.rate)} · ×{fmt(growth, 1)}
                            </b>
                        </div>
                        <Slider
                            id={`${idPrefix}-rcc-growth`}
                            min={0.5}
                            max={20}
                            step={0.5}
                            value={growth}
                            marks={{ 1: ' ', 10: ' ' }}
                            tooltip={{ formatter: v => `×${v}` }}
                            onChange={(v: number) => setGrowth(v)}
                        />
                    </div>

                    <dl className={s.rccStats}>
                        <div>
                            <dt>{t.tokensDay}</dt>
                            <dd>{tokens(now.tokens)} RCC</dd>
                        </div>
                        <div>
                            <dt>{t.costDay}</dt>
                            <dd>{money(now.tokens * buyPrice)}</dd>
                        </div>
                        <div>
                            <dt>{t.feePct}</dt>
                            <dd className={s.rccPct}>{fmt(now.pct, 1)}%</dd>
                        </div>
                    </dl>

                    <table className={s.rccTable}>
                        <thead>
                            <tr>
                                <th>{t.growth}</th>
                                <th>{t.rate}</th>
                                <th>{t.tokensDay}, RCC</th>
                                <th>{t.feePct}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {GROWTH_ROWS.map(g => {
                                const row = at(g)
                                return (
                                    <tr
                                        key={g}
                                        className={
                                            g === growth ? s.rccRowOn : undefined
                                        }
                                        onClick={() => setGrowth(g)}
                                    >
                                        <td>×{g}</td>
                                        <td>{usd(row.rate)}</td>
                                        <td>{tokens(row.tokens)}</td>
                                        <td>{fmt(row.pct, 1)}%</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>

                    <p className={s.rccNote}>{t.note}</p>
                </div>
            )}
        </div>
    )
}
